import {
  Article,
  ArticleAction,
  ArticleFooter,
  ArticleImage,
  ArticleTitle,
} from "@babackapp/customer-ui/components/article";
import { Badge } from "@babackapp/customer-ui/components/badge";
import { Button } from "@babackapp/customer-ui/components/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@babackapp/customer-ui/components/tooltip";
import { useAddLineItem } from "@babackapp/return-widget-components/hooks/use-add-line-item";
import { useDraftReturn } from "@babackapp/return-widget-components/hooks/use-draft-return";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import type { OrderLineItem } from "@babackapp/return-widget-components/types";
import { ChevronRight, InfoIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { LineItemSubtitle } from "./line-item-subtitle";
import { useNonReturnableLabel } from "./use-non-returnable-label";

type LineItemRowProps = {
  lineItem: OrderLineItem;
  orderId: string;
  shopId: string;
  currency?: string;
  isConnectedUser?: boolean;
  connectedUserTooltip?: string;
  /** Marks an article the shop excluded, which only a merchant can still select. */
  isNonReturnable?: boolean;
};

export function LineItemRow({
  lineItem,
  orderId,
  shopId,
  currency = "EUR",
  isConnectedUser = false,
  connectedUserTooltip,
  isNonReturnable = false,
}: LineItemRowProps) {
  const navigate = useNavigate();
  const t = useCustomText();
  const addLineItem = useAddLineItem(orderId, shopId);
  const draft = useDraftReturn(orderId);
  const isInDraft =
    draft?.lineItems.some((li) => li.lineItemId === lineItem.line_item_id) ??
    false;
  const getNonReturnableLabel = useNonReturnableLabel();

  const handleSelect = () => {
    if (!isInDraft) {
      addLineItem({
        lineItemId: lineItem.line_item_id,
        quantity: 1,
        reasonId: null,
        comment: null,
        imageUrls: [],
        resolution: null,
        resolutionOption: null,
        exchangeVariantId: null,
      });
    }
    navigate(
      `/return-widget/${shopId}/order/${orderId}/line-item/${lineItem.line_item_id}/reason`,
    );
  };

  return (
    <Article onClick={handleSelect} className="cursor-pointer">
      <ArticleImage
        src={lineItem.image}
        layoutId={`img-${lineItem.line_item_id}`}
      />
      <ArticleTitle>{lineItem.title}</ArticleTitle>
      <LineItemSubtitle lineItem={lineItem} currency={currency} />
      {isConnectedUser && lineItem.sku && (
        <ArticleFooter className="flex items-center gap-1.5">
          <span>{lineItem.sku}</span>
          <Tooltip>
            <TooltipTrigger asChild onClick={(e) => e.stopPropagation()}>
              <InfoIcon className="size-3.5 cursor-help" />
            </TooltipTrigger>
            <TooltipContent>{connectedUserTooltip}</TooltipContent>
          </Tooltip>
        </ArticleFooter>
      )}
      <ArticleAction>
        {isNonReturnable && (
          <Badge variant="secondary">{getNonReturnableLabel(lineItem)}</Badge>
        )}
        <Button
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleSelect();
          }}
        >
          {t("articleSelection.returnCta")}
          <ChevronRight />
        </Button>
      </ArticleAction>
    </Article>
  );
}
